/**
 * A share link carries `?loginHint=<address the share was sent to>`. The sign-in name MSAL reports is
 * often a different address for the same person (a university netid, or an alias such as
 * crimson.ua.edu vs law.ua.edu). Re-running login lands on that same account, so redirecting on every
 * mismatch never converges: the redirect cancels the first API call, returns to a URL that still has the
 * hint, and starts over. The hint is a request to offer the right account, not a requirement, so offer it
 * once per tab and then continue with the account the user signed in with.
 */
export const LOGIN_HINT_RELOGIN_KEY = "loginHintReloginFor";

export interface LoginHintCheck {
  loginHint: string;
  /** Number of MSAL accounts already signed in. */
  accountCount: number;
  /** Mail MSAL reports for the active account (its sign-in name), if any. */
  activeEmail?: string | null;
  /** Email on the user record returned by the API after a previous login, if any. */
  storedUserEmail?: string | null;
  /** The hint a re-login was already offered for in this tab (sessionStorage). */
  reloginOfferedFor?: string | null;
}

const same = (a?: string | null, b?: string) => !!a && a.toLowerCase() === b?.toLowerCase();

export const shouldReloginForLoginHint = ({
  loginHint,
  accountCount,
  activeEmail,
  storedUserEmail,
  reloginOfferedFor,
}: LoginHintCheck): boolean => {
  // Nobody is signed in: sign in (with the hint).
  if (accountCount === 0) return true;
  // The signed-in person is the recipient, by sign-in name or by the user record the API returned.
  if (same(activeEmail, loginHint) || same(storedUserEmail, loginHint)) return false;
  // A different identity: offer the re-login once, then stop so the redirect cannot loop.
  return !same(reloginOfferedFor, loginHint);
};
